
import window from 'bits/global'
import { buildUrlFromPath, goBack, reload } from 'bits/location'
import { Action, Component, Getter, State, Vue, Watch, namespace } from 'bits/vue'
import OzBackButton from 'library/v4/components/OzBackButton.vue'
import OzCheckBox from 'library/v4/components/OzCheckBox.vue'
import OzContainedButton, { OzContainedButtonColorScheme } from 'library/v4/components/OzContainedButton.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'
import OzInput from 'library/v4/components/OzInput.vue'
import OzLabelInput from 'library/v4/components/OzLabelInput.vue'
import OzLoadingText from 'library/v4/components/OzLoadingText.vue'
import OzPadletLogo from 'library/v4/components/OzPadletLogo.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from 'library/v4/components/OzPlainButton.vue'
import OzTextLink from 'library/v4/components/OzTextLink.vue'
import AuthActionMenu from 'vuecomponents/AuthActionMenu.vue'
import AuthBottomNav from 'vuecomponents/AuthBottomNav.vue'
import AuthLoginThirdPartyGroupButtons, {
  AuthLoginThirdPartyGroupButtonsType,
} from 'vuecomponents/AuthLoginThirdPartyGroupButtons.vue'
import GlobalAlertDialog from 'vuecomponents/GlobalAlertDialog.vue'
import GlobalConfirmationDialog from 'vuecomponents/GlobalConfirmationDialog.vue'
import GlobalSnackbar from 'vuecomponents/GlobalSnackbarVuex.vue'
import VisualViewportSizeMixin from 'vuemixins/visual_viewport_size_mixin'
import WindowSizeMixin from 'vuemixins/window_size_mixin'
import { LoginMethod } from 'vuexstore/landside'

const browserStore = namespace('browser')

@Component({
  components: {
    OzIcon,
    OzContainedButton,
    OzBackButton,
    OzLabelInput,
    OzInput,
    OzCheckBox,
    OzTextLink,
    OzLoadingText,
    OzPadletLogo,
    OzPlainButton,
    AuthLoginThirdPartyGroupButtons,
    AuthActionMenu,
    AuthBottomNav,
    GlobalAlertDialog,
    GlobalConfirmationDialog,
    GlobalSnackbar,
  },
  mixins: [WindowSizeMixin, VisualViewportSizeMixin],
})
export default class AuthSignupApp extends Vue {
  $refs!: {
    emailInput: OzInput
    passwordInput: OzInput
  }

  OzPlainButtonColorScheme = OzPlainButtonColorScheme
  OzPlainButtonSizePreset = OzPlainButtonSizePreset
  OzContainedButtonColorScheme = OzContainedButtonColorScheme
  AuthLoginThirdPartyGroupButtonsType = AuthLoginThirdPartyGroupButtonsType

  xActionMenu = false
  isEmailInputFocused = false
  isPasswordInputFocused = false
  isPasswordShown = false
  initialScrollY = 0
  reloadPage = reload

  @State email!: string
  @State isEmailVerified!: boolean
  @State password!: string
  @State emailValidationMessage!: string
  @State passwordValidationMessage!: string
  @State isAwaitingServerResponse!: boolean
  @State minimumPasswordLength!: number
  @State dir!: string
  @State isElectronApp!: boolean
  @Getter isEmailValid!: boolean
  @Getter isPasswordValid!: boolean
  @Getter thirdPartyAuthLoginMethods!: Record<string, LoginMethod>
  @Action setEmail!: (email: string) => void
  @Action setPassword!: (password: string) => void
  @Action setEmailValidationMessage!: (message: string) => void
  @Action setPasswordValidationMessage!: (message: string) => void
  @Action setIsEmailVerified!: (value: boolean) => void
  @Action continueSignupWithEmail!: () => Promise<void>
  @Action completeSignupWithPassword!: () => void
  @browserStore.Getter visualViewportHeight!: number
  @browserStore.Getter isSmallerThanTabletPortrait!: boolean
  @browserStore.Getter isMobile!: boolean
  @browserStore.Getter isTablet!: boolean
  @browserStore.Getter isDesktop!: boolean
  @browserStore.Getter isAndroid!: boolean

  // Header elements are hidden when the input is focused on android phones.
  get xHeader(): boolean {
    return !(
      this.isMobile &&
      this.isAndroid &&
      this.isSmallerThanTabletPortrait &&
      (this.isEmailInputFocused || this.isPasswordInputFocused)
    )
  }

  get isContinueButtonDisabled(): boolean {
    return !this.email.length || !!this.emailValidationMessage.length
  }

  get isCreateAccountButtonDisabled(): boolean {
    return !this.password.length || !!this.passwordValidationMessage.length
  }

  get loginPageUrl(): string {
    return buildUrlFromPath('/auth/login')
  }

  get xEmailVerificationScreen(): boolean {
    return !this.isEmailVerified
  }

  get termOfServiceAndPrivacyPolicyText(): string {
    const classes = 'oz-text-link-light dark:oz-text-link-dark'
    return this.__(
      'By signing up, you agree to our %{termsOfService} and %{privacyPolicy}. You also admit that you are beautiful.',
      {
        termsOfService: `<a href="https://legal.padlet.com/terms" target="_blank" class="${classes}">${this.__(
          'Terms of Service',
        )}</a>`,
        privacyPolicy: `<a href="https://legal.padlet.com/privacy" target="_blank" class="${classes}">${this.__(
          'Privacy Policy',
        )}</a>`,
      },
    )
  }

  @Watch('visualViewportHeight')
  respondToVisualViewportHeightChange(newValue: number, oldValue: number): void {
    if (this.isDesktop || newValue >= oldValue) return

    this.initialScrollY = window.scrollY

    // When people focus on the input, the keyboard will pop up
    // so the visual viewport height will be narrowed down.
    // we want to hide the keyboard when people try to scroll the page
    if (this.isEmailInputFocused) {
      document.addEventListener('scroll', this.blurEmailInputOnScroll)
    } else if (this.isPasswordInputFocused) {
      document.addEventListener('scroll', this.blurPasswordInputOnScroll)
    }
  }

  @Watch('xEmailVerificationScreen', { immediate: true })
  respondToScreenChange(newValue: boolean): void {
    // Only focus on current screen input field after a 1ms `setTimeout`,
    // because we need to wait for the input field to be shown in the DOM,
    // before we can focus on it.
    setTimeout(() => {
      this.$nextTick(() => {
        if (newValue) {
          this.$refs.emailInput.focusInput()
        } else {
          this.$refs.passwordInput.focusInput()
        }
      })
    }, 1)
  }

  @Watch('isAwaitingServerResponse')
  handleIsAwaitingServerResponse(value: boolean): void {
    const html = document.getElementsByTagName('html')[0]

    if (this.xEmailVerificationScreen) return

    // Animate the background color while waiting for the response
    // Note: Only do this when submitting the signup form
    if (value) {
      html.classList.add('dot-grid-loading')
    } else {
      html.classList.remove('dot-grid-loading')
    }
  }

  beforeDestroy(): void {
    window.removeEventListener('popstate', this.goBackEmailVerificationScreen)
  }

  onInputEmailInput(e): void {
    this.setEmail(e.target.value)

    // Reset the email validation message to enable the Continue button when typing on mobile and tablet
    if (this.emailValidationMessage.length) this.setEmailValidationMessage('')
  }

  onInputPasswordInput(e): void {
    this.setPassword(e.target.value)

    // Reset the password validation message to enable the Login button when typing on mobile and tablet
    if (this.passwordValidationMessage.length) this.setPasswordValidationMessage('')
  }

  onFocusInEmailInput(): void {
    this.isEmailInputFocused = true
  }

  onFocusInPasswordInput(): void {
    this.isPasswordInputFocused = true
  }

  onBlurEmailInput(): void {
    this.isEmailInputFocused = false
    document.removeEventListener('scroll', this.blurEmailInputOnScroll)
  }

  onBlurPasswordInput(): void {
    this.isPasswordInputFocused = false
    document.removeEventListener('scroll', this.blurPasswordInputOnScroll)
  }

  blurEmailInputOnScroll(): void {
    // Note: the scroll event is fired when people press a key on the soft keyboard only on iPad
    // we just blur the input only if the user scrolls past a certain threshold
    if (this.isExceededScrollableThreshold(window.scrollY)) {
      this.$refs.emailInput.blurInput()
    }
  }

  blurPasswordInputOnScroll(): void {
    // Note: the scroll event is fired when people press a key on the soft keyboard only on iPad
    // we just blur the input only if the user scrolls past a certain threshold
    if (this.isExceededScrollableThreshold(window.scrollY)) {
      this.$refs.passwordInput.blurInput()
    }
  }

  isExceededScrollableThreshold(currentWindowY: number): boolean {
    const SCROLLABLE_THRESHOLD = 40 // the height of the input field
    return (
      currentWindowY > this.initialScrollY + SCROLLABLE_THRESHOLD ||
      currentWindowY < this.initialScrollY - SCROLLABLE_THRESHOLD
    )
  }

  goBackEmailVerificationScreen(event: PopStateEvent | Event): void {
    if (event.type === 'click') {
      goBack()
    }

    this.isPasswordShown = false
    this.setIsEmailVerified(false)
    this.setPassword('')
    this.setPasswordValidationMessage('')
  }

  async submit(): Promise<void> {
    if (this.xEmailVerificationScreen) {
      await this.continueSignupWithEmail()

      if (this.isEmailVerified && !window.history.state?.didPushState) {
        // Enable the browser back button on signup page
        // didPushState: true to prevent pushing redundant states
        window.history.pushState({ didPushState: true }, '', window.location.href)
      }

      window.addEventListener('popstate', this.goBackEmailVerificationScreen)
    } else {
      this.completeSignupWithPassword()
    }
  }
}
